import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Lock, Eye, Sparkles, X, Filter, Search, Monitor, Tablet, Smartphone, Star } from "lucide-react";
import api from "@/lib/api";

/** Small stylized CSS thumbnail — derived from the template's tokens. */
function TemplateThumb({ tokens }) {
    const primary = tokens.primary || "#123B5D";
    const accent = tokens.accent || "#0F766E";
    const bg = tokens.bg || "#F5F8FA";
    const surface = tokens.surface || "#FFFFFF";
    const heroBg = tokens.hero_bg || primary;
    const heroFg = tokens.hero_fg || "#FFFFFF";
    const radius = tokens.border_radius || "8px";
    const variant = tokens.hero_variant || "split_photo_right";
    return (
        <div
            className="w-full aspect-[4/3] overflow-hidden relative"
            style={{ background: bg, borderRadius: radius }}
            aria-hidden
        >
            <div
                style={{
                    background: heroBg,
                    color: heroFg,
                    height: variant === "minimal_top" ? "26%" : variant === "full_bleed_image" ? "68%" : "44%",
                    padding: "8px 10px",
                    display: "flex",
                    alignItems: variant.includes("centered") || variant.includes("stacked") ? "center" : "flex-end",
                    justifyContent: variant.includes("centered") || variant.includes("stacked") ? "center" : "flex-start",
                    flexDirection: "column",
                    gap: "3px",
                }}
            >
                <div style={{ width: "50%", height: "6px", background: heroFg, opacity: 0.9, borderRadius: 2 }} />
                <div style={{ width: "35%", height: "4px", background: heroFg, opacity: 0.55, borderRadius: 2 }} />
                <div
                    style={{
                        width: "24px", height: "24px",
                        borderRadius: variant === "split_photo_left" ? radius : "50%",
                        background: heroFg, opacity: 0.85,
                        position: "absolute", top: "12%",
                        [variant === "split_photo_left" ? "left" : "right"]: variant.includes("centered") || variant.includes("stacked") ? "50%" : "10px",
                        transform: variant.includes("centered") || variant.includes("stacked") ? "translateX(-50%)" : "none",
                        display: variant === "minimal_top" ? "none" : "block",
                    }}
                />
            </div>
            <div style={{ padding: "8px", display: "flex", gap: "6px" }}>
                <div style={{ flex: 1, height: "22px", background: surface, borderRadius: 4, borderLeft: `3px solid ${accent}` }} />
                <div style={{ flex: 1, height: "22px", background: surface, borderRadius: 4, borderLeft: `3px solid ${primary}` }} />
            </div>
            <div style={{ padding: "0 8px 8px", display: "flex", gap: "6px" }}>
                <div style={{ flex: 1, height: "8px", background: surface, borderRadius: 3 }} />
                <div style={{ flex: 1, height: "8px", background: surface, borderRadius: 3 }} />
                <div style={{ flex: 1, height: "8px", background: surface, borderRadius: 3 }} />
            </div>
        </div>
    );
}

/** Real live iframe preview — renders the demo doctor's public page with a
 * template override so every card shows the actual site look at 19% scale.
 *
 *  - Iframe src is only set once the card scrolls near the viewport
 *    (600px root margin) and cleared when it scrolls far away, so the 162
 *    cards don't hold 162 iframes in memory at once.
 *  - Pointer-events disabled — the iframe never steals scroll or clicks.
 *  - sandbox restricts scripts but allows same-origin so the SSR page's
 *    minor client-side interactivity (mobile nav, stat counters) still runs.
 */
function TemplateIframePreview({ code, tokens, active }) {
    const wrapRef = useRef(null);
    const [near, setNear] = useState(false);
    useEffect(() => {
        if (!active) return;
        if (!wrapRef.current) return;
        const io = new IntersectionObserver(
            (entries) => setNear(entries[0].isIntersecting),
            { rootMargin: "600px" },
        );
        io.observe(wrapRef.current);
        return () => io.disconnect();
    }, [active]);

    const base = process.env.REACT_APP_BACKEND_URL || "";
    const src = active && near
        ? `${base}/api/public/dr/demo-anaya-mehta?template_preview=${encodeURIComponent(code)}&compact=1`
        : "about:blank";
    const bg = (tokens && tokens.bg) || "#F5F8FA";
    return (
        <div
            ref={wrapRef}
            className="w-full aspect-[4/3] overflow-hidden relative rounded-md"
            style={{ background: bg, border: "1px solid rgba(11,27,58,0.08)" }}
            aria-hidden
            data-testid={`template-iframe-wrap-${code}`}
        >
            <iframe
                src={src}
                title={`Preview ${code}`}
                loading="lazy"
                sandbox="allow-same-origin allow-scripts"
                scrolling="no"
                style={{
                    width: "1440px", height: "1080px",
                    border: 0,
                    transform: "scale(0.19)",
                    transformOrigin: "top left",
                    pointerEvents: "none",
                    background: "#fff",
                }}
                data-testid={`template-iframe-${code}`}
            />
        </div>
    );
}



/** Lazy-render wrapper — only paints the thumbnail once the card scrolls into view. */
function LazyCard({ children, testId }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!ref.current || visible) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisible(true);
                    io.disconnect();
                }
            },
            { rootMargin: "300px" },
        );
        io.observe(ref.current);
        return () => io.disconnect();
    }, [visible]);
    return (
        <div ref={ref} data-testid={testId} className="min-h-[260px]">
            {visible ? children : <div className="w-full aspect-[4/3] bg-slate-100 rounded-lg animate-pulse" />}
        </div>
    );
}

/** Preview modal with desktop/tablet/mobile viewport toggles. */
function PreviewModal({ code, onClose }) {
    const [viewport, setViewport] = useState("desktop"); // desktop | tablet | mobile
    const token = localStorage.getItem("ddi_token");
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const srcDoc = `<!doctype html><html><head><meta charset="utf-8"/><style>html,body{margin:0;background:#F5F7FB;font-family:Inter,sans-serif}#loading{padding:40px;text-align:center;color:#64748B}</style></head><body><div id="loading">Loading preview…</div><script>
      (async () => {
        try {
          const r = await fetch("${base}/api/v1/me/template/preview?template_code=${encodeURIComponent(code)}", {
            headers: {"Authorization": "Bearer ${token}"}
          });
          if (!r.ok) throw new Error("HTTP " + r.status);
          const html = await r.text();
          document.open(); document.write(html); document.close();
        } catch (e) {
          document.getElementById("loading").textContent = "Preview failed: " + e.message;
        }
      })();
    </script></body></html>`;

    const vpSize = {
        desktop: { width: "100%", maxWidth: "1280px" },
        tablet: { width: "768px", maxWidth: "768px" },
        mobile: { width: "390px", maxWidth: "390px" },
    }[viewport];

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="template-preview-modal" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-4 py-3 border-b border-[#E5E9F2] flex items-center gap-2 flex-wrap">
                    <Eye size={14} className="text-[#3A80F7]" />
                    <span className="text-[13px] font-semibold text-[#0F172A]">Live preview · {code}</span>
                    <div className="ml-4 flex items-center gap-1 bg-slate-50 rounded-md p-0.5" data-testid="preview-viewport-toggle">
                        {[
                            { k: "desktop", Icon: Monitor, label: "Desktop" },
                            { k: "tablet", Icon: Tablet, label: "Tablet" },
                            { k: "mobile", Icon: Smartphone, label: "Mobile" },
                        ].map(({ k, Icon, label }) => (
                            <button
                                key={k}
                                onClick={() => setViewport(k)}
                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold transition-colors ${
                                    viewport === k ? "bg-white text-[#3A80F7] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"
                                }`}
                                data-testid={`preview-viewport-${k}`}
                            >
                                <Icon size={12} /> {label}
                            </button>
                        ))}
                    </div>
                    <button onClick={onClose} className="ml-auto text-[#64748B] hover:text-[#DC2626]" data-testid="template-preview-close">
                        <X size={16} />
                    </button>
                </div>
                <div className="flex-1 overflow-auto bg-slate-100 p-4 flex justify-center items-start">
                    <iframe
                        srcDoc={srcDoc}
                        className="bg-white rounded-lg shadow border border-slate-200 transition-all"
                        style={{ ...vpSize, height: "calc(92vh - 90px)" }}
                        title="Template preview"
                    />
                </div>
            </div>
        </div>
    );
}


export default function TemplateGallery() {
    const [payload, setPayload] = useState(null);
    const [activeFamily, setActiveFamily] = useState("All");
    const [query, setQuery] = useState("");
    const [showLockedOnly, setShowLockedOnly] = useState(false);
    const [showNewOnly, setShowNewOnly] = useState(false);
    const [preview, setPreview] = useState(null);
    const [applying, setApplying] = useState(null);

    async function load() {
        try {
            const r = await api.get("/me/template");
            setPayload(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }
    useEffect(() => { load(); }, []);

    async function apply(code) {
        setApplying(code);
        try {
            await api.put("/me/template", { template_code: code });
            toast.success("Template selected — republish to apply on your public page");
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d && typeof d === "object" && d.code === "template_locked") {
                toast.error(`Locked — requires ${d.min_tier_name || `tier ${d.min_tier}`} or higher`);
            } else {
                toast.error(typeof d === "string" ? d : "Failed");
            }
        } finally { setApplying(null); }
    }

    const families = useMemo(() => {
        if (!payload) return [];
        return ["All", ...payload.families];
    }, [payload]);

    const filtered = useMemo(() => {
        if (!payload) return [];
        const q = query.trim().toLowerCase();
        const out = payload.templates.filter((t) => {
            if (activeFamily !== "All" && t.family !== activeFamily) return false;
            if (showLockedOnly && t.unlocked) return false;
            if (showNewOnly && !t.is_new) return false;
            if (!q) return true;
            return (
                (t.name || "").toLowerCase().includes(q) ||
                (t.code || "").toLowerCase().includes(q) ||
                (t.tagline || "").toLowerCase().includes(q) ||
                (t.family || "").toLowerCase().includes(q) ||
                (t.layout_variant || "").toLowerCase().includes(q)
            );
        });
        // Surface new templates first so they don't get lost among 162 existing.
        return [...out].sort((a, b) => {
            if (!!a.is_new === !!b.is_new) return 0;
            return a.is_new ? -1 : 1;
        });
    }, [payload, activeFamily, query, showLockedOnly, showNewOnly]);

    if (!payload) {
        return <div className="text-[#64748B] text-[13px]">Loading templates…</div>;
    }

    const flagshipCodes = new Set(["ddip_verified_pro", "ddip_teal", "ddip_purple", "ddip_sky"]);
    const LAYOUT_LABELS = {
        split_editorial: "Editorial Spread",
        timeline_first: "Journey First",
        card_grid: "Bento Cards",
        typographic_minimal: "Typographic",
    };

    return (
        <div data-testid="template-gallery">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <div>
                    <h2 className="text-[15px] font-semibold text-[#0F172A]">
                        Choose your template
                        <span className="text-[#94A3B8] font-normal text-[13px]"> · {filtered.length} of {payload.templates.length}</span>
                    </h2>
                    <div className="text-[11px] text-[#64748B] mt-0.5">
                        Your plan: <span className="font-semibold capitalize text-[#0F172A]">{payload.package_code || "—"}</span>
                    </div>
                </div>
                <div className="relative w-full sm:w-72">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search templates…"
                        className="w-full h-9 pl-9 pr-3 text-[13px] bg-white border border-[#E5E9F2] rounded-md focus:outline-none focus:border-[#3A80F7]"
                        data-testid="template-search-input"
                    />
                </div>
            </div>

            <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="template-family-tabs">
                <Filter size={13} className="text-[#94A3B8]" />
                {families.map((f) => {
                    const count = f === "All"
                        ? payload.templates.length
                        : payload.templates.filter((t) => t.family === f).length;
                    return (
                        <button
                            key={f}
                            onClick={() => setActiveFamily(f)}
                            className={
                                "px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors " +
                                (activeFamily === f
                                    ? "bg-[#3A80F7] text-white"
                                    : "bg-white text-[#334155] border border-[#E5E9F2] hover:border-[#3A80F7]")
                            }
                            data-testid={`family-tab-${f.toLowerCase()}`}
                        >
                            {f} <span className="opacity-60 ml-1">{count}</span>
                        </button>
                    );
                })}
                <label className="ml-2 flex items-center gap-1.5 text-[11px] text-[#64748B] cursor-pointer">
                    <input
                        type="checkbox"
                        checked={showLockedOnly}
                        onChange={(e) => setShowLockedOnly(e.target.checked)}
                        data-testid="template-locked-only"
                    />
                    Locked only
                </label>
                <label className="ml-1 flex items-center gap-1.5 text-[11px] text-[#B8862B] font-semibold cursor-pointer">
                    <input
                        type="checkbox"
                        checked={showNewOnly}
                        onChange={(e) => setShowNewOnly(e.target.checked)}
                        data-testid="template-new-only"
                    />
                    <Sparkles size={11} /> New only
                </label>
            </div>

            {filtered.length === 0 && (
                <div className="text-center py-12 text-[#64748B] text-[13px]" data-testid="template-empty">
                    No templates match your filters.
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="template-cards">
                {filtered.map((t) => {
                    const isSelected = payload.template_code === t.code;
                    const isFlagship = flagshipCodes.has(t.code);
                    const isNew = !!t.is_new;
                    const isGenerated = /_v\d+$/.test(t.code);
                    const layoutLabel = t.layout_variant && t.layout_variant !== "flagship"
                        ? LAYOUT_LABELS[t.layout_variant] : null;
                    return (
                        <LazyCard key={t.code} testId={`template-lazy-${t.code}`}>
                            <div
                                className={
                                    "ddi-card p-4 hover:border-[#3A80F7] hover:shadow-md transition-all relative group " +
                                    (isSelected ? "border-[#3A80F7] shadow-md" : "")
                                }
                                data-testid={`template-card-${t.code}`}
                            >
                                {isFlagship && (
                                    <span className="absolute top-2 left-2 z-10 bg-[#3A80F7] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" data-testid={`template-badge-flagship-${t.code}`}>
                                        <Star size={9} fill="currentColor" /> Flagship
                                    </span>
                                )}
                                {isNew && !isFlagship && (
                                    <span className="absolute top-2 left-2 z-10 bg-gradient-to-r from-[#D9A441] to-[#B8862B] text-white text-[9px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-0.5 shadow-sm" data-testid={`template-badge-new-${t.code}`}>
                                        <Sparkles size={9} /> NEW
                                    </span>
                                )}
                                {isGenerated && !isFlagship && !isNew && (
                                    <span className="absolute top-2 left-2 z-10 bg-white/90 border border-[#3A80F7]/30 text-[#3A80F7] text-[9px] font-bold px-1.5 py-0.5 rounded-full" data-testid={`template-badge-variant-${t.code}`}>
                                        Variant
                                    </span>
                                )}
                                <TemplateIframePreview code={t.code} tokens={t.style_tokens} active={true} />
                                <div className="mt-3 flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13.5px] font-semibold text-[#0F172A] truncate">{t.name}</div>
                                        <div className="text-[11px] text-[#94A3B8] truncate">{t.tagline}</div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1 shrink-0 justify-end max-w-[60%]">
                                        <span className="ddi-chip ddi-chip-neutral text-[9px]">{t.family}</span>
                                        {layoutLabel && (
                                            <span className="ddi-chip ddi-chip-info text-[9px] bg-[#EEF2FF] text-[#4338CA] whitespace-nowrap" data-testid={`template-layout-${t.code}`}>
                                                {layoutLabel}
                                            </span>
                                        )}
                                        {isSelected && <span className="ddi-chip ddi-chip-success text-[9px]">Current</span>}
                                        {!t.unlocked && <span className="ddi-chip ddi-chip-warning text-[9px] whitespace-nowrap"><Lock size={9} /> {t.min_tier_name}+</span>}
                                    </div>
                                </div>
                                <div className="mt-3 flex gap-1.5">
                                    <button
                                        onClick={() => setPreview(t.code)}
                                        disabled={!t.unlocked}
                                        className="ddi-btn-ghost text-[12px] flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid={`template-preview-${t.code}`}
                                    >
                                        <Eye size={12} /> Preview
                                    </button>
                                    {isSelected ? (
                                        <span className="ddi-btn-primary text-[12px] flex-1 justify-center opacity-60 pointer-events-none">
                                            <Check size={12} /> Selected
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => apply(t.code)}
                                            disabled={applying === t.code || !t.unlocked}
                                            className="ddi-btn-primary text-[12px] flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                                            data-testid={`template-select-${t.code}`}
                                        >
                                            {applying === t.code ? <Sparkles size={12} className="animate-pulse" /> : <Check size={12} />}
                                            {t.unlocked ? "Select" : "Locked"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </LazyCard>
                    );
                })}
            </div>

            {preview && <PreviewModal code={preview} onClose={() => setPreview(null)} />}
        </div>
    );
}
